import { useState, useEffect, useMemo } from 'react';
import * as Sentry from "@sentry/react";
import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { syncNormalizedTags } from '../lib/tags';
import { ASSET_MATURITY, ASSET_STATUS, ASSET_TYPE, IS_MOCK_DEMO } from '../constants';
import { isOfflineError } from '../lib/firestore';
import { mockConcepts } from '../mockData';
import { ASSET_USAGE_STATUS, deriveAssetUsageMetrics, isArchiveCandidate } from '../lib/assetUsage';

export function useAssets(user, globalSelectedTags = []) {
  const [concepts, setConcepts] = useState(() => (IS_MOCK_DEMO ? mockConcepts.map(c => ({
    ...c,
    title: c.title ?? '',
    body: c.body ?? '',
    tags: c.tags ?? [],
    type: c.type ?? ASSET_TYPE.KNOWLEDGE,
    status: c.status ?? ASSET_STATUS.DRAFT,
    maturity: c.maturity ?? ASSET_MATURITY.SEED,
    sourceNoteIds: c.sourceNoteIds ?? [],
    sourceInsightIds: c.sourceInsightIds ?? [],
    ...deriveAssetUsageMetrics(c),
  })) : []));
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [selectedAssetStatus, setSelectedAssetStatus] = useState('all');
  const [selectedAssetFilter, setSelectedAssetFilter] = useState('all');
  const [assetSearchText, setAssetSearchText] = useState('');

  useEffect(() => {
    if (IS_MOCK_DEMO || !user?.uid) return undefined;
    const conceptsQuery = query(
      collection(db, 'concepts'),
      where('ownerUid', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(conceptsQuery, (snapshot) => {
      setConcepts(snapshot.docs.map((doc) => {
        const data = doc.data();
        const usage = deriveAssetUsageMetrics(data);
        const fallbackLastReferencedAt = usage.lastReferencedAt ?? data?.metadata?.lastReferencedAt ?? data.lastReferencedAt ?? null;
        return {
          id: doc.id,
          ...data,
          title: data.title ?? '',
          body: data.body ?? '',
          tags: data.tags ?? [],
          type: data.type ?? ASSET_TYPE.KNOWLEDGE,
          status: data.status ?? ASSET_STATUS.DRAFT,
          maturity: data.maturity ?? ASSET_MATURITY.SEED,
          sourceNoteIds: data.sourceNoteIds ?? [],
          sourceInsightIds: data.sourceInsightIds ?? [],
          usageStatus: usage.usageStatus,
          referenceCount: usage.referenceCount,
          referencesLast30Days: usage.referencesLast30Days,
          lastReferencedAt: usage.lastReferencedAt,
          daysSinceLastReference: usage.daysSinceLastReference,
          createdDaysAgo: usage.createdDaysAgo,
          metadata: {
            ...(data.metadata || {}),
            referenceCount: usage.referenceCount,
            referencesLast30Days: usage.referencesLast30Days,
            lastReferencedAt: fallbackLastReferencedAt,
            usageRuleVersion: usage.usageRuleVersion,
          },
        };
      }));
    }, (error) => {
      console.error('Failed to subscribe concepts:', error);
      if (!isOfflineError(error)) {
        Sentry.captureException(error, { tags: { action: 'useAssets_onSnapshot' } });
        alert('知的資産の同期に失敗しました。Firestoreのルールを確認してください。');
      }
    });
  }, [user?.uid]);

  const selectedAsset = useMemo(() => (
    concepts.find((concept) => concept.id === selectedAssetId) || null
  ), [concepts, selectedAssetId]);

  const filteredAssets = useMemo(() => (
    concepts.filter((asset) => {
       const search = assetSearchText.trim().toLowerCase();
       const searchTarget = [asset.title, asset.body, ...(asset.tags || [])].filter(Boolean).join(' ').toLowerCase();
       const matchesSearch = !search || searchTarget.includes(search);
       const matchesStatus = selectedAssetStatus === 'all' || asset.status === selectedAssetStatus;
       const matchesTag = globalSelectedTags.length === 0 || globalSelectedTags.some((tag) => (asset.tags || []).includes(tag));

       const matchesFilter = (() => {
         if (selectedAssetFilter === 'dormant') return asset.usageStatus === ASSET_USAGE_STATUS.DORMANT;
         if (selectedAssetFilter === 'review') return asset.usageStatus === ASSET_USAGE_STATUS.COOLING;
         if (selectedAssetFilter === 'archive_candidate') return isArchiveCandidate(asset);
         return true;
       })();

       return matchesSearch && matchesStatus && matchesTag && matchesFilter;
    })
  ), [concepts, assetSearchText, selectedAssetStatus, globalSelectedTags, selectedAssetFilter]);


  const handleUpdateAssetMaturity = async (assetId, maturity) => {
    const asset = concepts.find(c => c.id === assetId);
    const nextRevisionCount = (asset?.metadata?.revisionCount || 0) + 1;
    if (IS_MOCK_DEMO) {
      setConcepts((current) => current.map((concept) => (
        concept.id === assetId
          ? {
            ...concept,
            maturity,
            updatedAt: new Date().toISOString(),
            metadata: {
              ...(concept.metadata || {}),
              revisionType: 'user_edit',
              revisionCount: nextRevisionCount,
              lastRevisedAt: new Date().toISOString(),
            }
          }
          : concept
      )));
      return;
    }

    try {
      await updateDoc(doc(db, 'concepts', assetId), {
        maturity,
        updatedAt: serverTimestamp(),
        'metadata.revisionType': 'user_edit',
        'metadata.revisionCount': increment(1),
        'metadata.lastRevisedAt': serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update asset maturity:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateAssetMaturity' }, extra: { assetId } });
    }
  };

  const handleUpdateAssetStatus = async (assetId, status) => {
    const asset = concepts.find(c => c.id === assetId);
    const nextRevisionCount = (asset?.metadata?.revisionCount || 0) + 1;
    if (IS_MOCK_DEMO) {
      setConcepts((current) => current.map((concept) => (
        concept.id === assetId
          ? {
            ...concept,
            status,
            updatedAt: new Date().toISOString(),
            metadata: {
              ...(concept.metadata || {}),
              revisionType: 'user_edit',
              revisionCount: nextRevisionCount,
              lastRevisedAt: new Date().toISOString(),
            }
          }
          : concept
      )));
      return;
    }

    try {
      await updateDoc(doc(db, 'concepts', assetId), {
        status,
        updatedAt: serverTimestamp(),
        'metadata.revisionType': 'user_edit',
        'metadata.revisionCount': increment(1),
        'metadata.lastRevisedAt': serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update asset status:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateAssetStatus' }, extra: { assetId } });
    }
  };

  const handleUpdateAssetTitle = async (assetId, title) => {
    const asset = concepts.find(c => c.id === assetId);
    const nextRevisionCount = (asset?.metadata?.revisionCount || 0) + 1;
    if (IS_MOCK_DEMO) {
      setConcepts((current) => current.map((concept) => (
        concept.id === assetId
          ? {
            ...concept,
            title,
            updatedAt: new Date().toISOString(),
            metadata: {
              ...(concept.metadata || {}),
              revisionType: 'user_edit',
              revisionCount: nextRevisionCount,
              lastRevisedAt: new Date().toISOString(),
            }
          }
          : concept
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'concepts', assetId), {
        title,
        updatedAt: serverTimestamp(),
        'metadata.revisionType': 'user_edit',
        'metadata.revisionCount': increment(1),
        'metadata.lastRevisedAt': serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update asset title:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateAssetTitle' }, extra: { assetId } });
    }
  };

  const handleUpdateAssetBody = async (assetId, body) => {
    const asset = concepts.find(c => c.id === assetId);
    const nextRevisionCount = (asset?.metadata?.revisionCount || 0) + 1;
    if (IS_MOCK_DEMO) {
      setConcepts((current) => current.map((concept) => (
        concept.id === assetId
          ? {
            ...concept,
            body,
            updatedAt: new Date().toISOString(),
            metadata: {
              ...(concept.metadata || {}),
              revisionType: 'user_edit',
              revisionCount: nextRevisionCount,
              lastRevisedAt: new Date().toISOString(),
            }
          }
          : concept
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'concepts', assetId), {
        body,
        updatedAt: serverTimestamp(),
        'metadata.revisionType': 'user_edit',
        'metadata.revisionCount': increment(1),
        'metadata.lastRevisedAt': serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update asset body:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateAssetBody' }, extra: { assetId } });
    }
  };

  const handleUpdateAssetType = async (assetId, type) => {
    const asset = concepts.find(c => c.id === assetId);
    const nextRevisionCount = (asset?.metadata?.revisionCount || 0) + 1;
    if (IS_MOCK_DEMO) {
      setConcepts((current) => current.map((concept) => (
        concept.id === assetId
          ? {
            ...concept,
            type,
            updatedAt: new Date().toISOString(),
            metadata: {
              ...(concept.metadata || {}),
              revisionType: 'user_edit',
              revisionCount: nextRevisionCount,
              lastRevisedAt: new Date().toISOString(),
            }
          }
          : concept
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'concepts', assetId), {
        type,
        updatedAt: serverTimestamp(),
        'metadata.revisionType': 'user_edit',
        'metadata.revisionCount': increment(1),
        'metadata.lastRevisedAt': serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update asset type:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateAssetType' }, extra: { assetId } });
    }
  };

  const handleUpdateAssetTags = async (assetId, tags) => {
    const asset = concepts.find(c => c.id === assetId);
    const nextRevisionCount = (asset?.metadata?.revisionCount || 0) + 1;
    const previousTags = asset?.tags || [];
    if (IS_MOCK_DEMO) {
      setConcepts((current) => current.map((concept) => (
        concept.id === assetId
          ? {
            ...concept,
            tags,
            updatedAt: new Date().toISOString(),
            metadata: {
              ...(concept.metadata || {}),
              revisionType: 'user_edit',
              revisionCount: nextRevisionCount,
              lastRevisedAt: new Date().toISOString(),
            }
          }
          : concept
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'concepts', assetId), {
        tags,
        updatedAt: serverTimestamp(),
        'metadata.revisionType': 'user_edit',
        'metadata.revisionCount': increment(1),
        'metadata.lastRevisedAt': serverTimestamp(),
      });
      await syncNormalizedTags(db, user?.uid, assetId, 'concept', tags, previousTags);
    } catch (error) {
      console.error('Failed to update asset tags:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateAssetTags' }, extra: { assetId } });
      throw error;
    }
  };

  return {
    concepts,
    setConcepts,
    selectedAssetId,
    setSelectedAssetId,
    selectedAssetStatus,
    setSelectedAssetStatus,
    assetSearchText,
    setAssetSearchText,
    selectedAssetFilter,
    setSelectedAssetFilter,
    selectedAsset,
    filteredAssets,
    handleUpdateAssetMaturity,
    handleUpdateAssetStatus,
    handleUpdateAssetTitle,
    handleUpdateAssetBody,
    handleUpdateAssetType,
    handleUpdateAssetTags,
  };
}
